import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, imageUrl } from "../lib/api";

export default function Gallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => setImages(r.data))
      .catch(() => setImages([]))
      .finally(() => setLoading(false));
  }, []);

  const close = useCallback(() => setLightboxIdx(null), []);
  const next = useCallback(
    () => setLightboxIdx((i) => (i === null ? null : (i + 1) % images.length)),
    [images.length]
  );
  const prev = useCallback(
    () =>
      setLightboxIdx((i) =>
        i === null ? null : (i - 1 + images.length) % images.length
      ),
    [images.length]
  );

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, close, next, prev]);

  return (
    <div data-testid="gallery-page" className="pt-32">
      <section className="px-6 md:px-10 pb-12">
        <div className="max-w-7xl mx-auto text-center">
          <p className="font-overline text-xs text-[#DE9E48] mb-4">— Gallery</p>
          <h1 className="font-display text-6xl md:text-8xl tracking-tighter leading-none">
            Encore <span className="italic text-[#DE9E48]">Moments.</span>
          </h1>
          <p className="font-body text-stone-300 mt-6 max-w-2xl mx-auto text-lg leading-relaxed">
            Explore photos from past performances and relive the stories that
            moved us all.
          </p>
          <div className="marquee w-24 mx-auto mt-10" />
        </div>
      </section>

      <section className="px-6 md:px-10 py-12 max-w-7xl mx-auto">
        {renderGallery(loading, images, setLightboxIdx)}
      </section>

      {lightboxIdx !== null && (
        <div className="lightbox" data-testid="gallery-lightbox" onClick={close}>
          <button
            data-testid="lightbox-close"
            onClick={close}
            className="absolute top-6 right-6 text-white/70 hover:text-[#DE9E48] z-10"
            aria-label="Close"
          >
            <X size={28} />
          </button>
          <button
            data-testid="lightbox-prev"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#DE9E48]"
            aria-label="Previous"
          >
            <ChevronLeft size={36} />
          </button>
          <img
            src={imageUrl(images[lightboxIdx].url)}
            alt={images[lightboxIdx].caption || "Encore moment"}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            data-testid="lightbox-next"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[#DE9E48]"
            aria-label="Next"
          >
            <ChevronRight size={36} />
          </button>
          {images[lightboxIdx].caption && (
            <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-stone-300 font-display italic text-lg">
              {images[lightboxIdx].caption}
            </p>
          )}
        </div>
      )}
    </div>
  );
}


function renderGallery(loading, images, setLightboxIdx) {
  if (loading) {
    return (
      <p className="text-stone-400 font-overline text-sm text-center py-20">
        Loading gallery…
      </p>
    );
  }
  if (images.length === 0) {
    return (
      <p
        data-testid="gallery-empty"
        className="text-stone-400 font-overline text-sm text-center py-20"
      >
        No images yet.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
      {images.map((img, i) => (
        <button
          key={img.id}
          data-testid={`gallery-item-${i}`}
          onClick={() => setLightboxIdx(i)}
          className={`gallery-card ${
            i % 7 === 0 ? "row-span-2 aspect-[3/5]" : "aspect-square"
          }`}
        >
          <img
            src={imageUrl(img.url)}
            alt={img.caption || `Performance ${i + 1}`}
            loading="lazy"
          />
        </button>
      ))}
    </div>
  );
}
